import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { BreadcrumbService } from 'app/core/navigation/breadcrumb.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import {
    BookmarkPromptDialogComponent,
    BookmarkPromptDialogResult,
} from 'app/layout/common/bookmark-prompt-dialog/bookmark-prompt-dialog.component';
import { Shortcut } from 'app/layout/common/shortcuts/shortcuts.types';
import { ShortcutsService } from 'app/layout/common/shortcuts/shortcuts.service';
import { Subject, combineLatest, filter, startWith, takeUntil } from 'rxjs';

@Component({
    selector: 'favorite-toggle',
    templateUrl: './favorite-toggle.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButtonModule, MatIconModule, MatTooltipModule],
})
export class FavoriteToggleComponent implements OnInit, OnDestroy {
    canFavorite = true;
    isFavorite = false;
    isLoading = false;

    private _navigationItems: FuseNavigationItem[] = [];
    private _currentFavorite: Shortcut | null = null;
    private _currentLink = '/';
    private _currentLabel = 'Current screen';
    private _currentIcon = 'heroicons_outline:star';
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private _router: Router,
        private _dialog: MatDialog,
        private _breadcrumbService: BreadcrumbService,
        private _navigationService: NavigationService,
        private _shortcutsService: ShortcutsService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this._navigationItems = this._flattenNavigation(
                    navigation.default ?? []
                );
            });

        combineLatest([
            this._shortcutsService.shortcuts$,
            this._router.events.pipe(
                filter((event) => event instanceof NavigationEnd),
                startWith(null)
            ),
        ])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(([shortcuts]) => {
                this._refreshRouteState(shortcuts);
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    toggleFavorite(): void {
        if (!this.canFavorite || this.isLoading) {
            return;
        }

        if (this._currentFavorite) {
            this.isLoading = true;

            this._shortcutsService
                .delete(this._currentFavorite.id)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: () => {
                        this.isLoading = false;
                        this._changeDetectorRef.markForCheck();
                    },
                    error: () => {
                        this.isLoading = false;
                        this._changeDetectorRef.markForCheck();
                    },
                });

            return;
        }

        this._dialog
            .open(BookmarkPromptDialogComponent, {
                width: '600px',
                maxHeight: '90vh',
                data: {
                    mode: 'create',
                    label: this._currentLabel,
                    icon: this._currentIcon,
                },
            })
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result: BookmarkPromptDialogResult | undefined) => {
                if (!result) {
                    this._changeDetectorRef.markForCheck();
                    return;
                }

                this.isLoading = true;

                this._shortcutsService
                    .create({
                        label: result.label,
                        description: '',
                        icon: result.icon,
                        link: this._currentLink,
                        useRouter: true,
                    })
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: () => {
                            this.isLoading = false;
                            this._changeDetectorRef.markForCheck();
                        },
                        error: () => {
                            this.isLoading = false;
                            this._changeDetectorRef.markForCheck();
                        },
                    });
            });
    }

    private _refreshRouteState(shortcuts: Shortcut[]): void {
        this._currentLink = this._normalizeRouteLink(this._router.url);
        this.canFavorite = this._isFavoritableRoute(this._currentLink);

        const breadcrumbs = this._breadcrumbService.build();
        const routeMatch = this._getBestNavigationMatch(this._currentLink);
        const fallbackLabel = routeMatch?.title ?? this._labelFromLink(this._currentLink);
        const routeLabel =
            breadcrumbs.length > 0
                ? breadcrumbs[breadcrumbs.length - 1].label
                : fallbackLabel;
        const ancestorLabels = breadcrumbs
            .slice(0, -1)
            .map((crumb) => crumb.label)
            .filter((label) => !!label);

        this._currentLabel = this._buildContextualLabel(
            routeLabel,
            ancestorLabels
        );

        this._currentIcon = routeMatch?.icon ?? 'heroicons_outline:star';

        this._currentFavorite =
            shortcuts.find(
                (shortcut) =>
                    this._normalizeRouteLink(shortcut.link) === this._currentLink
            ) ?? null;

        this.isFavorite = !!this._currentFavorite;

        this._changeDetectorRef.markForCheck();
    }

    private _normalizeRouteLink(link: string): string {
        let normalized = (link ?? '').trim();

        const hashIndex = normalized.indexOf('#');
        if (hashIndex >= 0) {
            normalized = normalized.slice(0, hashIndex);
        }

        const queryIndex = normalized.indexOf('?');
        if (queryIndex >= 0) {
            normalized = normalized.slice(0, queryIndex);
        }

        normalized = normalized.trim();

        if (!normalized.startsWith('/')) {
            normalized = '/' + normalized;
        }

        if (normalized.length > 1) {
            normalized = normalized.replace(/\/+$/, '');
        }

        return normalized || '/';
    }

    private _isFavoritableRoute(link: string): boolean {
        const blockedPrefixes = [
            '/sign-in',
            '/sign-out',
            '/forgot-password',
            '/reset-password',
            '/confirmation-required',
            '/unlock-session',
            '/unauthorized',
            '/order-form',
        ];

        return !blockedPrefixes.some((prefix) => link.startsWith(prefix));
    }

    private _getBestNavigationMatch(link: string):
        | { title?: string; icon?: string }
        | null {
        const candidates = this._navigationItems
            .filter((item) => item.link && link.startsWith(item.link))
            .sort((a, b) => (b.link?.length ?? 0) - (a.link?.length ?? 0));

        if (candidates.length === 0) {
            return null;
        }

        return {
            title: candidates[0].title,
            icon: candidates[0].icon,
        };
    }

    private _flattenNavigation(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return items.flatMap((item) => {
            if (
                (item.type === 'group' || item.type === 'collapsable') &&
                item.children
            ) {
                return this._flattenNavigation(item.children);
            }

            return [item];
        });
    }

    private _labelFromLink(link: string): string {
        const segment = link.split('/').filter(Boolean).pop() ?? 'screen';

        return segment
            .split('-')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

    private _buildContextualLabel(
        label: string,
        ancestorLabels: string[]
    ): string {
        if (!this._isGenericLabel(label) || ancestorLabels.length === 0) {
            return label;
        }

        return `${ancestorLabels[ancestorLabels.length - 1]} - ${label}`;
    }

    private _isGenericLabel(label: string): boolean {
        const genericLabels = new Set([
            'Details',
            'Detail',
            'Price',
            'Edit',
            'New',
            'Add Supplier',
            'Products',
            'Users',
            'Events',
            'Packages',
            'Orders',
            'Stand Details',
            'Company Details',
        ]);

        return genericLabels.has(label);
    }
}
